/**
 * apps/web :: components/paywall/Paywall
 *
 * Blocked-surface paywall (ROADS-445 FE-01). Replaces the route content
 * when an authenticated user without an active entitlement attempts a
 * paid surface (training pages, exam, rule-diff browse, /whats-next).
 *
 * Two surfaces:
 *   1. **Full picker** — 4 V0 tier cards (Class C $20, CDL $49,
 *      Tourist $15, Outbound $15). Headline: "Your free trial has
 *      ended". Default for any user without prior entitlement history.
 *   2. **Renew** — single card pinned to the user's most-recently-
 *      expired tier with a "Renew $X" CTA. We never carry the prior
 *      state/country into the URL because pricing is per-tier; the
 *      user re-confirms their state on checkout.
 *
 * The "Add a card" CTA routes to `/checkout` — FE-02 ships the actual
 * Stripe Elements flow. Until then, /checkout shows a placeholder
 * "coming this week" message; that's wired in App.tsx.
 *
 * ROADS-436 status-agnostic: the picker NEVER asks citizenship or
 * residency. Tier names are state-centric (Class C / CDL) or
 * trip-centric (Tourist / Outbound), never user-status.
 *
 * Pricing source-of-truth: ROADS-445 Epic body. If pricing changes,
 * update PAYWALL_TIER_PRICES below + the i18n keys.
 */
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lock } from "lucide-react";

import TierCard from "@/components/paywall/TierCard";
import UpgradeCallout, {
  creditEstimateUsd,
  daysRemainingFor,
} from "@/components/paywall/UpgradeCallout";
import type { BillingProvider } from "@/lib/billing-provider-api";
import type {
  Entitlement,
  EntitlementTier,
} from "@/lib/entitlement-api";

/**
 * V0 pricing catalog. Lives here (not in design tokens) because the
 * literal price IS the product contract — changing it requires a
 * deliberate code change, not a tokens.json drift.
 */
export interface PaywallTierSpec {
  tier: EntitlementTier;
  /** Numeric price in USD (display only — Stripe holds the canonical value). */
  priceUsd: number;
  /** i18n key for the tier name (under `paywall.tiers.*.name`). */
  nameKey: string;
  /** i18n key for the tier description (under `paywall.tiers.*.description`). */
  descriptionKey: string;
  /** i18n key for the cadence suffix; null when there is no per-X cadence. */
  cadenceKey: string | null;
}

export const PAYWALL_TIER_CATALOG: readonly PaywallTierSpec[] = [
  {
    tier: "class_c",
    priceUsd: 20,
    nameKey: "paywall.tiers.classC.name",
    descriptionKey: "paywall.tiers.classC.description",
    cadenceKey: "paywall.tiers.classC.cadence",
  },
  {
    tier: "cdl",
    priceUsd: 49,
    nameKey: "paywall.tiers.cdl.name",
    descriptionKey: "paywall.tiers.cdl.description",
    cadenceKey: "paywall.tiers.cdl.cadence",
  },
  {
    tier: "tourist_in",
    priceUsd: 15,
    nameKey: "paywall.tiers.tourist.name",
    descriptionKey: "paywall.tiers.tourist.description",
    cadenceKey: "paywall.tiers.tourist.cadence",
  },
  {
    tier: "tourist_out",
    priceUsd: 15,
    nameKey: "paywall.tiers.outbound.name",
    descriptionKey: "paywall.tiers.outbound.description",
    cadenceKey: "paywall.tiers.outbound.cadence",
  },
];

export interface PaywallProps {
  /**
   * The user's entitlement history. When provided, the paywall checks
   * for any expired-but-previously-held entitlement and collapses to
   * the renew-tier path. Undefined / empty → full picker.
   */
  priorEntitlements?: readonly Entitlement[];
  /** Server clock at probe-fetch time. Drives the "expired" decision. */
  nowMs?: number;
  /**
   * Test seam: invoked on tier-CTA click. Defaults to `useNavigate()`-
   * driven routing to `/checkout?tier=...`.
   */
  onSelect?: (tier: EntitlementTier) => void;
  /**
   * Wave 12 Pillar B addition: when the paywall is rendered from a
   * tier-specific gated surface (e.g. /cdl/practice mounted under
   * `<PaidSurfaceGate tier="cdl">`), narrow the picker to the focused
   * tier. The state-aware tier-specific copy comes from i18n
   * (`paywall.tiers.<tier>.*`); the CTA carries `state` through to
   * checkout when supplied. Undefined → full picker (legacy default).
   */
  focusTier?: EntitlementTier;
  /** Optional state-code passed through to /checkout when focusTier is set. */
  focusState?: string | null;
  /**
   * ROADS-451 PR-4: active billing lane.
   *
   * - `"stripe"` (default): legacy Stripe lane — render the FE-04
   *    credit-math callout when an active tourist entitlement exists.
   * - `"paddle"`: Merchant-of-Record cutover — Paddle handles proration
   *    server-side on `PATCH /subscriptions/{id}`, so the client-side
   *    credit estimate is suppressed (a misleading number would be
   *    worse than no number).
   *
   * Optional + defaulting to `"stripe"` preserves backwards
   * compatibility for the many call sites that don't yet have the
   * billing-provider context. PR-5 will introduce a BillingProvider
   * context provider so the Paywall and PaidSurfaceGate resolve the
   * lane consistently without prop-drilling.
   *
   * TODO ROADS-451 PR-5: pass billingProvider from a context provider.
   */
  billingProvider?: BillingProvider;
}

/**
 * Pick the most-recently-expired entitlement, if any. Defines "renew"
 * eligibility: we collapse to a single card when there's exactly one
 * historical tier — anything more nuanced (multi-state Class C,
 * tourist+outbound history) falls back to the full picker.
 */
function pickRenewalTier(
  priorEntitlements: readonly Entitlement[],
  nowMs: number,
): EntitlementTier | null {
  const expired = priorEntitlements
    .filter((e) => e.expires_at_ms <= nowMs)
    .sort((a, b) => b.expires_at_ms - a.expires_at_ms);
  if (expired.length === 0) return null;
  // Only collapse when ALL prior history is the same tier — otherwise
  // present the full picker so the user can pick a different track.
  const tier = expired[0].tier;
  const allSameTier = expired.every((e) => e.tier === tier);
  return allSameTier ? tier : null;
}

/**
 * Pick the active tourist entitlement (if any) eligible for the FE-04
 * upgrade-credit flow. Returns null when the user has no active tourist
 * row, or when their active tourist row was funded by something other
 * than a one-time purchase (subscription / refund / comp / trial). The
 * latest-expiring active tourist row wins when there are multiple.
 */
function pickUpgradeFromTourist(
  priorEntitlements: readonly Entitlement[],
  nowMs: number,
): Entitlement | null {
  const candidates = priorEntitlements.filter(
    (e) =>
      (e.tier === "tourist_in" || e.tier === "tourist_out") &&
      e.expires_at_ms > nowMs &&
      e.source === "purchase",
  );
  if (candidates.length === 0) return null;
  return [...candidates].sort((a, b) => b.expires_at_ms - a.expires_at_ms)[0];
}

export default function Paywall({
  priorEntitlements,
  nowMs,
  onSelect,
  focusTier,
  focusState,
  // ROADS-451 PR-4: optional billing-lane prop. Defaults to "stripe" so
  // every existing call site keeps its current credit-callout behavior.
  // TODO ROADS-451 PR-5: pass billingProvider from a context provider.
  billingProvider = "stripe",
}: PaywallProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // When a tier-specific gate renders this paywall (e.g. /cdl/* under
  // `<PaidSurfaceGate tier="cdl">`), short-circuit the picker to a
  // focused, single-tier card with state-aware unlock copy.
  if (focusTier === "cdl") {
    return <CdlFocusedPaywall focusState={focusState ?? null} />;
  }

  const renewalTier =
    priorEntitlements && nowMs !== undefined
      ? pickRenewalTier(priorEntitlements, nowMs)
      : null;

  // FE-04 upgrade-eligibility check: when the user holds an active
  // tourist purchase, surface the credit callout above the tier grid +
  // pass the source id through to /checkout so the resident-tier CTA
  // ships the upgrade-aware request body.
  const upgradeSource =
    priorEntitlements && nowMs !== undefined
      ? pickUpgradeFromTourist(priorEntitlements, nowMs)
      : null;
  const upgradeDays =
    upgradeSource && nowMs !== undefined
      ? daysRemainingFor(upgradeSource.expires_at_ms, nowMs)
      : 0;
  const upgradeCreditUsd = creditEstimateUsd(upgradeDays);

  const handleSelect = (tier: EntitlementTier) => {
    if (onSelect) {
      onSelect(tier);
      return;
    }
    const search = new URLSearchParams({ tier });
    // Only attach the upgrade id when the destination is a non-tourist
    // tier — the backend rejects tourist→tourist as a 400.
    if (
      upgradeSource &&
      tier !== "tourist_in" &&
      tier !== "tourist_out"
    ) {
      search.set("upgrade_from_entitlement_id", upgradeSource.id);
    }
    navigate(`/checkout?${search.toString()}`);
  };

  const headline = renewalTier
    ? t("paywall.headlineRenew", { defaultValue: "Welcome back — renew to keep going" })
    : t("paywall.headline", { defaultValue: "Your free trial has ended" });
  const subhead = renewalTier
    ? t("paywall.subheadRenew", {
        defaultValue: "Pick up exactly where you left off. No re-onboarding, no lost progress.",
      })
    : t("paywall.subhead", {
        defaultValue: "Add a card to continue. We accept all major cards; cancel anytime.",
      });

  const visibleTiers = renewalTier
    ? PAYWALL_TIER_CATALOG.filter((spec) => spec.tier === renewalTier)
    : PAYWALL_TIER_CATALOG;

  return (
    <section
      data-testid="paywall"
      aria-labelledby="paywall-heading"
      className="mx-auto max-w-5xl px-md py-lg"
    >
      <div className="flex items-center gap-sm text-amber-700 mb-sm">
        <Lock aria-hidden="true" size={18} />
        <span className="text-xs font-semibold uppercase tracking-wide">
          {t("paywall.eyebrow", { defaultValue: "Subscription required" })}
        </span>
      </div>
      <h1
        id="paywall-heading"
        className="text-2xl font-bold text-slate-900 sm:text-3xl"
      >
        {headline}
      </h1>
      <p className="mt-sm text-base text-slate-600 max-w-2xl">{subhead}</p>

      {/*
        ROADS-451 PR-4: the credit-math callout is meaningful only on
        the Stripe lane — Paddle proration runs server-side and we have
        no client-side estimate. On the Paddle lane we suppress the
        component entirely; a neutral blurb lives on the /checkout
        page itself where the upgrade context is concrete.
      */}
      {billingProvider === "stripe" && upgradeSource ? (
        <div className="mt-md">
          <UpgradeCallout
            daysRemaining={upgradeDays}
            creditUsd={upgradeCreditUsd}
          />
        </div>
      ) : null}

      <div
        data-testid="paywall-tier-grid"
        className={`mt-lg grid gap-md ${
          visibleTiers.length === 1
            ? "max-w-sm"
            : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
        }`}
      >
        {visibleTiers.map((spec) => (
          <TierCard
            key={spec.tier}
            tier={spec.tier}
            name={t(spec.nameKey, { defaultValue: spec.tier })}
            priceLabel={`$${spec.priceUsd}`}
            cadenceLabel={
              spec.cadenceKey
                ? t(spec.cadenceKey, { defaultValue: "" }) || undefined
                : undefined
            }
            description={t(spec.descriptionKey, { defaultValue: "" })}
            isRenewal={renewalTier === spec.tier}
            onSelect={handleSelect}
          />
        ))}
      </div>

      <p className="mt-md text-xs text-slate-500">
        {t("paywall.disclaimer", {
          defaultValue:
            "Prices in USD. Subscriptions auto-renew until cancelled. Cancel anytime in Settings.",
        })}
      </p>
    </section>
  );
}

/**
 * Focused paywall variant rendered when a tier-specific gate (e.g.
 * `<PaidSurfaceGate tier="cdl">`) drops the user on the paywall. CDL
 * is a fixed-duration purchase ($49 per state · 6 months — NOT a
 * subscription); the copy here mirrors the public pricing-catalog
 * exactly so the price + cadence stay in lock-step across web +
 * marketing.
 *
 * Note: the four feature bullets live in i18n (`cdl.paywall.featureN`)
 * — the marketing pricing-catalog at `apps/marketing/src/lib/pricing-
 * catalog.ts` is the upstream source of the price + cadence facts;
 * this component restates them in the in-app i18n surface so the
 * shipped UI does not depend on cross-workspace imports.
 */
function CdlFocusedPaywall({
  focusState,
}: {
  focusState: string | null;
}): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tierLabel = t("cdl.paywall.tierName", { defaultValue: "CDL" });
  const price = t("cdl.paywall.price", { defaultValue: "$49" });
  const cadence = t("cdl.paywall.cadence", {
    defaultValue: "per state · 6 months",
  });
  const stateForCta = focusState ?? "";
  const ctaLabel = focusState
    ? t("cdl.paywall.unlockCta", { state: focusState })
    : t("cdl.paywall.tierName", { defaultValue: "Unlock CDL" });

  const features = [
    t("cdl.paywall.feature1"),
    t("cdl.paywall.feature2"),
    t("cdl.paywall.feature3"),
    t("cdl.paywall.feature4"),
  ];

  const handleClick = () => {
    const search = new URLSearchParams({ tier: "cdl" });
    if (focusState) search.set("state", focusState);
    navigate(`/checkout?${search.toString()}`);
  };

  return (
    <section
      data-testid="paywall"
      aria-labelledby="paywall-heading"
      className="mx-auto max-w-2xl px-md py-lg"
    >
      <div className="flex items-center gap-sm text-amber-700 mb-sm">
        <Lock aria-hidden="true" size={18} />
        <span className="text-xs font-semibold uppercase tracking-wide">
          {t("paywall.eyebrow", { defaultValue: "Subscription required" })}
        </span>
      </div>
      <h1
        id="paywall-heading"
        className="text-2xl font-bold text-slate-900 sm:text-3xl"
      >
        {t("cdl.license.unlock.heading", {
          defaultValue: "Unlock CDL practice + exam mode",
        })}
      </h1>
      <div
        data-testid="paywall-tier-card-cdl"
        className="mt-lg flex flex-col gap-sm rounded-2xl border border-slate-200 bg-white p-md shadow-sm"
      >
        <div className="flex items-baseline justify-between gap-sm">
          <h2 className="text-base font-semibold text-slate-900">{tierLabel}</h2>
          <div className="flex items-baseline gap-1 text-slate-900">
            <span data-testid="cdl-paywall-price" className="text-2xl font-bold">
              {price}
            </span>
            <span data-testid="cdl-paywall-cadence" className="text-xs text-slate-500">
              {cadence}
            </span>
          </div>
        </div>
        <ul
          data-testid="cdl-paywall-features"
          className="mt-sm list-disc space-y-xs pl-5 text-sm text-slate-700"
        >
          {features.map((f, idx) => (
            <li key={idx}>{f}</li>
          ))}
        </ul>
        <p className="mt-sm text-xs text-slate-500">
          {t("cdl.paywall.purchaseNote", {
            defaultValue: "Fixed-duration purchase, not a subscription.",
          })}
        </p>
        <button
          type="button"
          data-testid="paywall-tier-cta-cdl"
          onClick={handleClick}
          className="mt-sm inline-flex h-11 items-center justify-center rounded-xl bg-teal-600 px-md text-sm font-semibold text-white hover:bg-teal-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700"
          aria-label={ctaLabel}
          data-state={stateForCta}
        >
          {ctaLabel}
        </button>
      </div>
    </section>
  );
}
